import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TrendingUp, Users, FolderOpen, ClipboardList, Shield, BarChart3, Settings, LogOut, DollarSign, FileSpreadsheet, FileCheck, MessageSquare, Phone, Bell, CreditCard, CalendarClock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { debtorService, metricsService, portfolioService, requestsService, api, massWhatsAppService, exportService, companyService } from '@/lib/api';
import PortfolioManager from '@/components/PortfolioManager';
import TeamManager from '@/components/TeamManager';
import CompanyAnalytics from '@/components/CompanyAnalytics';
import CampaignManager from '@/components/CampaignManager';
import BroadcastManager from '@/components/BroadcastManager';
import DocumentRequestsManager from '@/components/DocumentRequestsManager';
import { RecurringBillingDialog } from '@/components/RecurringBillingDialog';
import DescargosPanel from '@/components/DescargosPanel';
import VeritelPanel from '@/components/VeritelPanel';
import CobranzaPanel from '@/components/CobranzaPanel';
import DebtorEditDialog from '@/components/DebtorEditDialog';
import PaymentHistoryDialog from '@/components/PaymentHistoryDialog';
import RequestsPanel from '@/components/RequestsPanel';
import NotificationBell from '@/components/NotificationBell';
import WorkspaceSwitcher from '@/components/WorkspaceSwitcher';
import DebtorsTab from '@/components/company/DebtorsTab';
import AgendaTab from '@/components/company/AgendaTab';
import DebtorCrmSheet from '@/components/crm/DebtorCrmSheet';
import { crmService } from '@/lib/api';
import MassWhatsAppDialog from '@/components/company/MassWhatsAppDialog';
import PaymentsPanel from '@/components/PaymentsPanel';

const CompanyDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [metrics, setMetrics] = useState(null);
  const [debtors, setDebtors] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState(null);
  const [activeTab, setActiveTab] = useState('deudores');
  const [focusRequestId, setFocusRequestId] = useState(null);
  const clearFocusRequest = useCallback(() => setFocusRequestId(null), []);
  const [crmDebtorId, setCrmDebtorId] = useState(null);
  const [crmStates, setCrmStates] = useState([]);
  const [crmStateFilter, setCrmStateFilter] = useState(null);
  const [focusTaskId, setFocusTaskId] = useState(null);
  const clearFocusTask = useCallback(() => setFocusTaskId(null), []);

  const handleNotificationNavigate = (notif) => {
    const requestId = notif.metadata?.request_id;
    if (notif.type === 'crm_task_due') {
      setActiveTab('agenda');
      setFocusTaskId(notif.metadata?.task_id || null);
    } else if (notif.type === 'payment_report' || notif.type === 'certificate_request') {
      setActiveTab('solicitudes');
      setFocusRequestId(requestId || null);
      loadData();
    } else if (notif.type === 'document_request') {
      setActiveTab('documentos');
    } else if (notif.type === 'payment_received') {
      setActiveTab('pagos');
    } else if (notif.debtor_name) {
      setSelectedPortfolioId(null);
      setSearchTerm(notif.debtor_name);
      setActiveTab('deudores');
    }
  };
  const [userRole, setUserRole] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);
  const [customDebtorFields, setCustomDebtorFields] = useState([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDebtors, setTotalDebtors] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const PAGE_SIZE = 50;

  // Edit debtor
  const [showEditDebtor, setShowEditDebtor] = useState(false);
  const [editingDebtor, setEditingDebtor] = useState(null);

  // Recurring billing
  const [showRecurring, setShowRecurring] = useState(false);
  const [recurringDebtor, setRecurringDebtor] = useState(null);

  // Column visibility
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = localStorage.getItem('macropago_columns');
      return saved ? JSON.parse(saved) : { name: true, portfolio: true, dominio: true, actas: true, deuda: true, quita: true };
    } catch { return { name: true, portfolio: true, dominio: true, actas: true, deuda: true, quita: true }; }
  });

  const toggleColumn = (col) => {
    setVisibleColumns(prev => {
      const next = { ...prev, [col]: !prev[col] };
      localStorage.setItem('macropago_columns', JSON.stringify(next));
      return next;
    });
  };

  const COLUMN_DEFS = [
    { key: 'dni', label: 'DNI' }, { key: 'cuit', label: 'CUIT' }, { key: 'code', label: 'Codigo' },
    { key: 'name', label: 'Nombre' }, { key: 'portfolio', label: 'Cartera' }, { key: 'dominio', label: 'Dominio' },
    { key: 'actas', label: 'Actas' }, { key: 'deuda', label: 'Deuda' }, { key: 'quita', label: 'C/Quita' },
    { key: 'email', label: 'Email' }, { key: 'phone', label: 'Telefono' }, { key: 'address', label: 'Direccion' },
    { key: 'city', label: 'Ciudad' }, { key: 'province', label: 'Provincia' }, { key: 'debt_date', label: 'Fecha Deuda' },
    { key: 'vto_monto', label: 'Vto. Monto' }, { key: 'vto_quita', label: 'Vto. Quita' }, { key: 'tags', label: 'Etiquetas' },
    ...customDebtorFields.map(f => ({ key: `custom_${f.field_id || f.key}`, label: f.field_name || f.label })),
  ];

  // Mass WhatsApp
  const [selectedDebtors, setSelectedDebtors] = useState([]);
  const [tenantSlug, setTenantSlug] = useState('');
  const [showMassWhatsApp, setShowMassWhatsApp] = useState(false);
  const [whatsAppMessage, setWhatsAppMessage] = useState('Hola {nombre}, te recordamos que tienes una deuda de {deuda}. Puedes consultar y pagar en: {link}');
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);

  // Payment history
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [selectedDebtorPayments, setSelectedDebtorPayments] = useState(null);

  useEffect(() => {
    if (location.state?.user) {
      setUserRole(location.state.user.role);
      setUserPermissions(location.state.user.permissions);
      setUserEmail(location.state.user.email);
      loadData();
      return;
    }
    const checkAuth = async () => {
      try {
        const response = await api.get('/auth/me');
        const user = response.data;
        if (!['company_admin', 'manager', 'supervisor'].includes(user.role)) { navigate('/login'); return; }
        setUserRole(user.role);
        setUserPermissions(user.permissions);
        setUserEmail(user.email);
        loadData();
      } catch { navigate('/login'); } finally { setLoading(false); }
    };
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, location.state]);

  const loadData = async () => {
    try {
      const [metricsResponse, debtorsResponse, portfoliosResponse, requestsResponse, fieldsResponse, settingsResponse] = await Promise.all([
        metricsService.getMetrics(),
        debtorService.listDebtors({ portfolio_id: selectedPortfolioId, page: currentPage, page_size: PAGE_SIZE, search: searchTerm || undefined, crm_state: crmStateFilter || undefined }),
        portfolioService.list(),
        requestsService.listRequests(),
        companyService.getDebtorFields().catch(() => ({ data: { fields: [] } })),
        api.get('/company/settings').catch(() => ({ data: {} }))
      ]);
      crmService.settings().then((r) => setCrmStates(r.data.account_states || [])).catch(() => {});
      setMetrics(metricsResponse.data);
      setDebtors(debtorsResponse.data.items);
      setTotalDebtors(debtorsResponse.data.total);
      setTotalPages(debtorsResponse.data.total_pages);
      setPortfolios(portfoliosResponse.data);
      setRequests(requestsResponse.data);
      if (settingsResponse.data?.slug) setTenantSlug(settingsResponse.data.slug);
      const fields = fieldsResponse.data?.fields || [];
      setCustomDebtorFields(fields.filter(f => f.is_custom || f.is_standard === false));
    } catch { toast.error('Error al cargar datos'); } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!loading) loadDebtors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPortfolioId, currentPage, searchTerm, crmStateFilter]);

  const loadDebtors = async () => {
    try {
      const response = await debtorService.listDebtors({ portfolio_id: selectedPortfolioId, page: currentPage, page_size: PAGE_SIZE, search: searchTerm || undefined, crm_state: crmStateFilter || undefined });
      setDebtors(response.data.items);
      setTotalDebtors(response.data.total);
      setTotalPages(response.data.total_pages);
    } catch (error) { console.error('Error loading debtors:', error); }
  };

  const refreshDashboard = async () => {
    try {
      const [metricsResponse] = await Promise.all([metricsService.getMetrics()]);
      setMetrics(metricsResponse.data);
      await loadDebtors();
    } catch (error) { console.error('Error refreshing dashboard:', error); }
  };

  const handleExport = async () => {
    try {
      toast.loading('Exportando...');
      const response = await exportService.exportDebtors(selectedPortfolioId);
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `clientes_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.dismiss();
      toast.success('Archivo exportado');
    } catch { toast.dismiss(); toast.error('Error al exportar'); }
  };

  const handleMassWhatsApp = async () => {
    if (selectedDebtors.length === 0) { toast.error('Selecciona al menos un cliente'); return; }
    setSendingWhatsApp(true);
    try {
      const result = await massWhatsAppService.send(selectedDebtors, whatsAppMessage);
      toast.success(`Enviados: ${result.data.sent}, Fallidos: ${result.data.failed}`);
      setShowMassWhatsApp(false);
      setSelectedDebtors([]);
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al enviar mensajes'); } finally { setSendingWhatsApp(false); }
  };

  const toggleDebtorSelection = (debtorId) => setSelectedDebtors(prev => prev.includes(debtorId) ? prev.filter(id => id !== debtorId) : [...prev, debtorId]);
  const toggleSelectAll = () => { selectedDebtors.length === debtors.length ? setSelectedDebtors([]) : setSelectedDebtors(debtors.map(d => d.id)); };

  const handleBulkDelete = async () => {
    if (selectedDebtors.length === 0) return;
    if (!window.confirm(`Eliminar ${selectedDebtors.length} deudor(es)? Esta accion no se puede deshacer.`)) return;
    try {
      toast.loading('Eliminando clientes...');
      const result = await debtorService.bulkDelete(selectedDebtors);
      toast.dismiss();
      toast.success(result.data.message);
      setSelectedDebtors([]);
      setCurrentPage(1);
      loadData();
    } catch { toast.dismiss(); toast.error('Error al eliminar clientes'); }
  };

  const handleDeleteAll = async () => {
    const confirmText = `ELIMINAR TODOS`;
    const input = window.prompt(`Esto eliminará TODOS los ${totalDebtors} clientes y sus datos asociados.\n\nEscribí "${confirmText}" para confirmar:`);
    if (input !== confirmText) {
      if (input !== null) toast.error('Texto de confirmación incorrecto');
      return;
    }
    try {
      toast.loading('Eliminando todos los clientes...');
      const result = await debtorService.deleteAll();
      toast.dismiss();
      toast.success(result.data.message);
      setSelectedDebtors([]);
      setCurrentPage(1);
      loadData();
    } catch { toast.dismiss(); toast.error('Error al eliminar clientes'); }
  };

  const openEditModal = async (debtor) => {
    try { const res = await portfolioService.list(); setPortfolios(res.data || []); } catch (e) { console.error('Error loading portfolios:', e); }
    setEditingDebtor({ ...debtor });
    setShowEditDebtor(true);
  };

  const handleLogout = async () => {
    try { await api.post('/auth/logout'); } catch (e) { console.error('Logout error:', e); }
    navigate('/login');
  };

  if (loading) {
    return (<div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="skeleton w-96 h-96 rounded-xl"></div></div>);
  }

  return (
    <div className="min-h-screen bg-slate-50/80">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="flex justify-between items-center h-[60px]">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-gradient-to-br from-slate-800 to-slate-950 rounded-lg flex items-center justify-center shadow-sm">
                <TrendingUp className="w-5 h-5 text-teal-400" />
              </div>
              <WorkspaceSwitcher onSwitch={() => { setLoading(true); loadData(); }} />
            </div>
            <div className="flex gap-1.5 items-center">
              <NotificationBell onNavigate={handleNotificationNavigate} />
              <Button variant="ghost" size="sm" className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 gap-1.5 h-9 px-3 rounded-lg" onClick={() => navigate('/dashboard/settings')} data-testid="company-settings-button">
                <Settings className="w-4 h-4" />
                <span className="hidden sm:inline text-sm">Configuracion</span>
              </Button>
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 h-9 w-9 p-0 rounded-lg" onClick={handleLogout} data-testid="logout-button"><LogOut className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-6" data-testid="company-dashboard">

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="group relative bg-white rounded-2xl border border-slate-100 p-5 hover:shadow-md transition-all duration-200 overflow-hidden" data-testid="metric-debtors">
            <div className="absolute top-0 right-0 w-24 h-24 bg-teal-50 rounded-full -mr-8 -mt-8 group-hover:scale-110 transition-transform duration-300" />
            <div className="relative">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 bg-teal-50 rounded-xl flex items-center justify-center">
                  <Users className="w-[18px] h-[18px] text-teal-600" />
                </div>
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Clientes</span>
              </div>
              <p className="text-2xl font-bold text-slate-900 tracking-tight">{metrics?.total_debtors || 0}</p>
            </div>
          </div>

          <div className="group relative bg-white rounded-2xl border border-slate-100 p-5 hover:shadow-md transition-all duration-200 overflow-hidden" data-testid="metric-portfolios">
            <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-50 rounded-full -mr-8 -mt-8 group-hover:scale-110 transition-transform duration-300" />
            <div className="relative">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 bg-indigo-50 rounded-xl flex items-center justify-center">
                  <FolderOpen className="w-[18px] h-[18px] text-indigo-600" />
                </div>
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Carteras</span>
              </div>
              <p className="text-2xl font-bold text-slate-900 tracking-tight">{portfolios?.length || 0}</p>
            </div>
          </div>

          <div className="group relative bg-white rounded-2xl border border-slate-100 p-5 hover:shadow-md transition-all duration-200 overflow-hidden" data-testid="metric-debt">
            <div className="absolute top-0 right-0 w-24 h-24 bg-rose-50 rounded-full -mr-8 -mt-8 group-hover:scale-110 transition-transform duration-300" />
            <div className="relative">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 bg-rose-50 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-[18px] h-[18px] text-rose-600" />
                </div>
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Deuda Total</span>
              </div>
              <p className="text-2xl font-bold text-slate-900 tracking-tight">${metrics?.total_debt?.toLocaleString('es-AR', {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}</p>
            </div>
          </div>

          <div className="group relative bg-white rounded-2xl border border-slate-100 p-5 hover:shadow-md transition-all duration-200 overflow-hidden" data-testid="metric-collected">
            <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-50 rounded-full -mr-8 -mt-8 group-hover:scale-110 transition-transform duration-300" />
            <div className="relative">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 bg-emerald-50 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-[18px] h-[18px] text-emerald-600" />
                </div>
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Recuperado</span>
              </div>
              <p className="text-2xl font-bold text-slate-900 tracking-tight">${metrics?.total_collected?.toLocaleString('es-AR', {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}</p>
              <div className="mt-1.5 flex items-center gap-1.5">
                <span className="text-[11px] font-medium text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-md">{metrics?.recovery_rate || 0}%</span>
                <span className="text-[11px] text-slate-400">tasa recupero</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs - Vertical Sidebar Layout */}
        <Tabs value={activeTab} onValueChange={setActiveTab} orientation="vertical" className="flex gap-5">
          <div className="w-52 shrink-0 sticky top-4 self-start">
            <TabsList className="bg-white border border-slate-100 rounded-2xl flex-col h-fit p-1.5 items-stretch shadow-sm">
              <p className="px-3 pt-2 pb-1 text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Gestion</p>
              <TabsTrigger value="deudores" className="justify-start rounded-xl h-9 text-[13px] font-medium text-slate-600 hover:bg-slate-50 data-[state=active]:bg-slate-900 data-[state=active]:text-white data-[state=active]:shadow-sm" data-testid="clientes-tab"><Users className="w-4 h-4 mr-2.5 shrink-0" />Clientes</TabsTrigger>
              <TabsTrigger value="agenda" className="justify-start rounded-xl h-9 text-[13px] font-medium text-slate-600 hover:bg-slate-50 data-[state=active]:bg-slate-900 data-[state=active]:text-white data-[state=active]:shadow-sm" data-testid="agenda-tab"><CalendarClock className="w-4 h-4 mr-2.5 shrink-0" />Agenda</TabsTrigger>
              <TabsTrigger value="campanas" className="justify-start rounded-xl h-9 text-[13px] font-medium text-slate-600 hover:bg-slate-50 data-[state=active]:bg-slate-900 data-[state=active]:text-white data-[state=active]:shadow-sm" data-testid="campaigns-tab"><FileSpreadsheet className="w-4 h-4 mr-2.5 shrink-0" />Campanas</TabsTrigger>
              <TabsTrigger value="carteras" className="justify-start rounded-xl h-9 text-[13px] font-medium text-slate-600 hover:bg-slate-50 data-[state=active]:bg-slate-900 data-[state=active]:text-white data-[state=active]:shadow-sm"><FolderOpen className="w-4 h-4 mr-2.5 shrink-0" />Carteras</TabsTrigger>
              <TabsTrigger value="documentos" className="justify-start rounded-xl h-9 text-[13px] font-medium text-slate-600 hover:bg-slate-50 data-[state=active]:bg-slate-900 data-[state=active]:text-white data-[state=active]:shadow-sm" data-testid="document-requests-tab"><FileCheck className="w-4 h-4 mr-2.5 shrink-0" />Actas/Facturas</TabsTrigger>

              <div className="my-1 mx-3 border-t border-slate-100" />
              <p className="px-3 pt-1 pb-1 text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Comunicacion</p>
              <TabsTrigger value="difusiones" className="justify-start rounded-xl h-9 text-[13px] font-medium text-slate-600 hover:bg-slate-50 data-[state=active]:bg-slate-900 data-[state=active]:text-white data-[state=active]:shadow-sm" data-testid="difusiones-tab"><MessageSquare className="w-4 h-4 mr-2.5 shrink-0" />Difusiones</TabsTrigger>
              <TabsTrigger value="cobranza" className="justify-start rounded-xl h-9 text-[13px] font-medium text-slate-600 hover:bg-slate-50 data-[state=active]:bg-slate-900 data-[state=active]:text-white data-[state=active]:shadow-sm" data-testid="cobranza-tab"><Bell className="w-4 h-4 mr-2.5 shrink-0" />Cobranza</TabsTrigger>
              <TabsTrigger value="veritel" className="justify-start rounded-xl h-9 text-[13px] font-medium text-slate-600 hover:bg-slate-50 data-[state=active]:bg-slate-900 data-[state=active]:text-white data-[state=active]:shadow-sm" data-testid="veritel-tab"><Phone className="w-4 h-4 mr-2.5 shrink-0" />Verificar Tel.</TabsTrigger>

              <div className="my-1 mx-3 border-t border-slate-100" />
              <p className="px-3 pt-1 pb-1 text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Operaciones</p>
              <TabsTrigger value="descargos" className="justify-start rounded-xl h-9 text-[13px] font-medium text-slate-600 hover:bg-slate-50 data-[state=active]:bg-slate-900 data-[state=active]:text-white data-[state=active]:shadow-sm" data-testid="descargos-tab"><ClipboardList className="w-4 h-4 mr-2.5 shrink-0" />Descargos</TabsTrigger>
              <TabsTrigger value="solicitudes" className="justify-start rounded-xl h-9 text-[13px] font-medium text-slate-600 hover:bg-slate-50 data-[state=active]:bg-slate-900 data-[state=active]:text-white data-[state=active]:shadow-sm">
                <ClipboardList className="w-4 h-4 mr-2.5 shrink-0" />Solicitudes
                {requests.filter(r => r.status === 'pending').length > 0 && <Badge className="ml-auto bg-amber-100 text-amber-700 text-[10px] h-5 min-w-5 px-1 rounded-full">{requests.filter(r => r.status === 'pending').length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="pagos" className="justify-start rounded-xl h-9 text-[13px] font-medium text-slate-600 hover:bg-slate-50 data-[state=active]:bg-slate-900 data-[state=active]:text-white data-[state=active]:shadow-sm" data-testid="pagos-tab"><CreditCard className="w-4 h-4 mr-2.5 shrink-0" />Pagos MP</TabsTrigger>

              <div className="my-1 mx-3 border-t border-slate-100" />
              <p className="px-3 pt-1 pb-1 text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Informes</p>
              <TabsTrigger value="analytics" className="justify-start rounded-xl h-9 text-[13px] font-medium text-slate-600 hover:bg-slate-50 data-[state=active]:bg-slate-900 data-[state=active]:text-white data-[state=active]:shadow-sm"><BarChart3 className="w-4 h-4 mr-2.5 shrink-0" />Analytics</TabsTrigger>
              {userRole === 'company_admin' && (
                <>
                  <div className="my-1 mx-3 border-t border-slate-100" />
                  <p className="px-3 pt-1 pb-1 text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Admin</p>
                  <TabsTrigger value="equipo" className="justify-start rounded-xl h-9 text-[13px] font-medium text-slate-600 hover:bg-slate-50 data-[state=active]:bg-slate-900 data-[state=active]:text-white data-[state=active]:shadow-sm"><Shield className="w-4 h-4 mr-2.5 shrink-0" />Equipo</TabsTrigger>
                </>
              )}
            </TabsList>
          </div>

          <div className="flex-1 min-w-0">

          <TabsContent value="deudores" className="mt-0">
            <DebtorsTab
              debtors={debtors} portfolios={portfolios} totalDebtors={totalDebtors}
              totalPages={totalPages} currentPage={currentPage} setCurrentPage={setCurrentPage}
              searchTerm={searchTerm} setSearchTerm={setSearchTerm}
              selectedPortfolioId={selectedPortfolioId} setSelectedPortfolioId={setSelectedPortfolioId}
              selectedDebtors={selectedDebtors} toggleDebtorSelection={toggleDebtorSelection} toggleSelectAll={toggleSelectAll}
              visibleColumns={visibleColumns} toggleColumn={toggleColumn}
              showColumnConfig={showColumnConfig} setShowColumnConfig={setShowColumnConfig}
              COLUMN_DEFS={COLUMN_DEFS} customDebtorFields={customDebtorFields}
              tenantSlug={tenantSlug} PAGE_SIZE={PAGE_SIZE}
              onExport={handleExport} onBulkDelete={handleBulkDelete} onDeleteAll={handleDeleteAll}
              onShowMassWhatsApp={() => setShowMassWhatsApp(true)}
              onEditDebtor={openEditModal}
              onShowPaymentHistory={(d) => { setSelectedDebtorPayments(d); setShowPaymentHistory(true); }}
              onShowRecurring={(d) => { setRecurringDebtor(d); setShowRecurring(true); }}
              onLoadData={loadData}
              onOpenCrm={(d) => setCrmDebtorId(d.id)}
              crmStates={crmStates} crmStateFilter={crmStateFilter}
              setCrmStateFilter={(v) => { setCrmStateFilter(v); setCurrentPage(1); }}
            />
          </TabsContent>

          <TabsContent value="agenda" className="mt-0"><AgendaTab onOpenDebtor={(id) => setCrmDebtorId(id)} focusTaskId={focusTaskId} onFocusHandled={clearFocusTask} /></TabsContent>

          <TabsContent value="campanas" className="mt-0"><CampaignManager onImportComplete={refreshDashboard} /></TabsContent>
          <TabsContent value="difusiones" className="mt-0"><BroadcastManager /></TabsContent>
          <TabsContent value="documentos" className="mt-0"><DocumentRequestsManager /></TabsContent>
          <TabsContent value="carteras" className="mt-0"><PortfolioManager onPortfolioSelect={(id) => { setSelectedPortfolioId(id); setActiveTab('deudores'); }} selectedPortfolioId={selectedPortfolioId} /></TabsContent>
          <TabsContent value="analytics" className="mt-0"><CompanyAnalytics /></TabsContent>
          <TabsContent value="solicitudes" className="mt-0"><RequestsPanel requests={requests} onUpdate={loadData} focusRequestId={focusRequestId} onFocusHandled={clearFocusRequest} /></TabsContent>
          {userRole === 'company_admin' && <TabsContent value="equipo" className="mt-0"><TeamManager /></TabsContent>}
          <TabsContent value="descargos" className="mt-0"><DescargosPanel /></TabsContent>
          <TabsContent value="veritel" className="mt-0"><VeritelPanel /></TabsContent>
          <TabsContent value="cobranza" className="mt-0"><CobranzaPanel /></TabsContent>
          <TabsContent value="pagos" className="mt-0"><PaymentsPanel /></TabsContent>
          </div>
        </Tabs>
      </div>

      {/* Dialogs */}
      <DebtorEditDialog open={showEditDebtor} onOpenChange={setShowEditDebtor} debtor={editingDebtor} portfolios={portfolios} customDebtorFields={customDebtorFields} onSuccess={loadData} />
      <PaymentHistoryDialog open={showPaymentHistory} onOpenChange={setShowPaymentHistory} debtor={selectedDebtorPayments} onUpdate={loadData} />
      <RecurringBillingDialog open={showRecurring} onOpenChange={setShowRecurring} debtor={recurringDebtor} onUpdate={loadData} />
      <MassWhatsAppDialog open={showMassWhatsApp} onOpenChange={setShowMassWhatsApp} selectedCount={selectedDebtors.length} message={whatsAppMessage} setMessage={setWhatsAppMessage} onSend={handleMassWhatsApp} sending={sendingWhatsApp} />
      <DebtorCrmSheet debtorId={crmDebtorId} open={!!crmDebtorId} onClose={() => setCrmDebtorId(null)} onChanged={loadDebtors} tenantSlug={tenantSlug} currentUserEmail={userEmail} />
    </div>
  );
};

export default CompanyDashboard;
